import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { mensagemAmigavel } from '@/lib/mensagemAmigavel';
import ListaMaquininhas from './ListaMaquininhas';
import ListaCaixas from './ListaCaixas';
import { normalizarCaixa, normalizarTerminal } from './utils';
import type { CaixaDoEvento, InfoMaquininhas, TerminalCaixa } from './types';

interface Props {
  eventoId: number;
}

type Bruto = Record<string, unknown>;

interface RespostaTerminais {
  ok?: boolean;
  terminais?: unknown[];
  mensagem?: string;
  aviso?: string;
  conta_do_evento?: boolean;
  da_conta_do_evento?: number;
}

const INFO_VAZIA: InfoMaquininhas = {
  contaDoEvento: false,
  daContaDoEvento: 0,
  mensagem: null,
  aviso: null,
};

// Bloco "Caixas e maquininhas" da aba Loja do painel.
// Só lê e chama o que já existe: caixa_terminais, caixa_caixas_do_evento e a
// Edge Function caixa-cobrar.
export default function BlocoCaixasMaquininhas({ eventoId }: Props) {
  const [terminais, setTerminais] = useState<TerminalCaixa[]>([]);
  const [caixas, setCaixas] = useState<CaixaDoEvento[]>([]);
  const [info, setInfo] = useState<InfoMaquininhas>(INFO_VAZIA);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    let ativo = true;

    async function carregar() {
      setErro(null);
      try {
        const [resTerm, resCaixa] = await Promise.all([
          supabase.rpc('caixa_terminais', { p_evento: eventoId }),
          supabase.rpc('caixa_caixas_do_evento', { p_evento: eventoId }),
        ]);
        if (resTerm.error) throw resTerm.error;
        if (resCaixa.error) throw resCaixa.error;
        if (!ativo) return;
        setTerminais(((resTerm.data as Bruto[]) || []).map(normalizarTerminal));
        setCaixas(((resCaixa.data as Bruto[]) || []).map(normalizarCaixa));
      } catch (e) {
        if (ativo) setErro(mensagemAmigavel(e));
      } finally {
        if (ativo) setCarregando(false);
      }

      // A função do caixa também lista as maquininhas da conta do evento.
      // Se ela não responder, a lista acima continua valendo.
      try {
        const { data } = await supabase.functions.invoke('caixa-cobrar', {
          body: { acao: 'terminais', evento_id: eventoId },
        });
        if (!ativo || !data) return;
        const r = data as RespostaTerminais;
        setInfo({
          contaDoEvento: r.conta_do_evento === true,
          daContaDoEvento: Number(r.da_conta_do_evento) || 0,
          mensagem: r.mensagem?.trim() || null,
          aviso: r.aviso?.trim() || null,
        });
        if (Array.isArray(r.terminais) && r.terminais.length > 0) {
          setTerminais(r.terminais.map((t) => normalizarTerminal(t as Bruto)));
          setCarregando(false);
        }
      } catch {
        /* segue com a lista da RPC */
      }
    }

    carregar();
    return () => {
      ativo = false;
    };
  }, [eventoId, reload]);

  const recarregar = useCallback(() => setReload((r) => r + 1), []);

  const atualizarInfo = useCallback((parcial: Partial<InfoMaquininhas>) => {
    setInfo((atual) => ({ ...atual, ...parcial }));
  }, []);

  return (
    <div className="mt-4 space-y-4">
      <div className="px-1">
        <div className="flex items-center gap-2">
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary-50 text-primary-600">
            <i className="ri-store-3-line" />
          </span>
          <h2 className="text-[18px] font-bold text-foreground-950">Caixas e maquininhas</h2>
        </div>
        <p className="mt-2 text-[14px] text-foreground-500">
          Ligue a maquininha da Point ao Se Liga e crie os caixas do evento — sem chamar o suporte.
        </p>
      </div>

      {erro ? (
        <div className="rounded-3xl bg-white p-10 text-center shadow-[0_6px_18px_rgba(20,25,60,.06)]">
          <p className="text-[15px] text-foreground-800">{erro}</p>
          <button
            type="button"
            onClick={recarregar}
            className="mt-4 cursor-pointer whitespace-nowrap rounded-2xl bg-primary-500 px-5 py-3 text-sm font-semibold text-white shadow-lg shadow-primary-500/30 transition-colors hover:bg-primary-600"
          >
            Tentar de novo
          </button>
        </div>
      ) : (
        <>
          <ListaMaquininhas
            eventoId={eventoId}
            terminais={terminais}
            carregando={carregando}
            info={info}
            aoRecarregar={recarregar}
            aoAtualizarInfo={atualizarInfo}
          />
          <ListaCaixas
            eventoId={eventoId}
            caixas={caixas}
            terminais={terminais}
            carregando={carregando}
            aoRecarregar={recarregar}
          />
        </>
      )}
    </div>
  );
}